import { Button } from "@mui/material"
import { useEffect, useReducer, useRef, useState } from "react"
import { useSetRecoilState } from "recoil"
import { sleep } from "../../../util"
import { UploadBox } from "../components"
import SnackAlerts, { snackAlert } from "../../../components/SnackAlerts"
import {
  Mode,
  SansaSerialCommandReader,
} from "../../../utils/serial/SansaSerialCommandReader"
import {
  writeAtCommandFrame,
  writeAtCommandFrameRawBytes,
} from "../../../utils/serial"
import {
  hexToUint8Array,
  uint8ArrayToString,
} from "../../../utils/serial/bytes"
import { makeAuthorizedRequest } from "../../../utils/api"

export const usbVendorId = 0x0483
export const usbProductId = 0x5710
export const baudRate = 115200

type DeviceDetailsParams = {
  port: SerialPort
  refreshPorts: () => void
  registerReader: (reader: SansaSerialCommandReader) => void
}

enum DeviceStatuses {
  CONNECTED = "connected",
  UNLOCKED = "unlocked",
  UNLOCKING = "unlocking",
  NRF_MODE = "nRF pass through mode",
  ERASING = "erasing certs",
  ERASED = "erased certs",
  SENDIND_CERTS = "sending certs",
  SENT_CERTS = "sent certs",
  NOT_CONNECTED = "not connected",
}

enum CertificateType {
  CA_CERT = 0,
  DEV_CERT = 1,
  PRIV_KEY = 2,
}

enum CertificateCommand {
  WRITE = 0,
  LIST = 1,
  READ = 2,
  DELETE = 3,
}

const SEC_TAG = 12345678

const detectAndStartConnection = async (port: SerialPort) => {
  const reader = new SansaSerialCommandReader(port, "AT_COMMAND")

  return { reader }
}

export const useScrollLogToTop = ({
  consoleTextarea,
  logOutput,
}: {
  consoleTextarea: React.RefObject<HTMLDivElement>
  logOutput: string
}) => {
  // Scroll to top of textarea when logOutput changes
  useEffect(() => {
    const area = consoleTextarea?.current
    if (area !== null) {
      area.scrollTop = area.scrollHeight
    }
  }, [consoleTextarea, logOutput])
}

export default function DeviceDetailsRoute({
  port,
  refreshPorts,
  registerReader,
}: DeviceDetailsParams) {
  const setSnackAlert = useSetRecoilState(snackAlert)
  const setErrorMsg = useSetRecoilState(snackAlert)
  const [connected, setConnected] = useState(false)
  const [currentMode, setCurrentMode] = useState<Mode>("AT_COMMAND")
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatuses>(
    DeviceStatuses.NOT_CONNECTED
  )
  const deviceStatusRef = useRef(deviceStatus)

  const [podSerial, setPodSerial] = useState<string>("")
  const podSerialRef = useRef(podSerial)

  const readerRef = useRef<SansaSerialCommandReader>()

  const consoleTextarea = useRef<HTMLDivElement>(null)
  const [logOutput, appendLogOutput] = useReducer(
    (oldOutput: string, newOutput: string) => `${oldOutput}${newOutput}\n`,
    ""
  )

  useScrollLogToTop({ consoleTextarea, logOutput })

  const setupReader = async () => {
    // try catch?

    await port.open({
      baudRate,
      flowControl: "hardware",
      bufferSize: 1024 * 1024 * 10,
      stopBits: 1,
      dataBits: 8,
      parity: "none",
    })

    if (port.readable === null) {
      throw new Error("reader is null")
    }

    const { reader } = await detectAndStartConnection(port)

    // Setup reader event handlers
    reader.onModeChange = (mode) => setCurrentMode(mode)

    reader.onAtCommandResponse = (value) => {
      console.log("@response:", value)
      appendLogOutput(value)

      if (value.includes("Starting up nRF pass through mode")) {
        setDeviceStatus(DeviceStatuses.NRF_MODE)
      }
      if (value.includes("Pod Serial Number: ")) {
        const serial = value.split(": ")[1].replace(/\s/g, "")
        setPodSerial(serial)
      }
      if (value.includes("@unlock")) {
        const unlockSecondPart = value.split("unlock: ")[1]
        if (
          uint8ArrayToString(hexToUint8Array(unlockSecondPart)).includes(
            "Unlock success"
          )
        ) {
          setDeviceStatus(DeviceStatuses.UNLOCKED)
          setSnackAlert({
            open: true,
            severity: "success",
            message: "Unlock Success",
          })
        } else {
          setDeviceStatus(DeviceStatuses.UNLOCKED)
          makeAuthorizedRequest(
            `devices/${podSerialRef.current}/unlock`,
            "POST",
            {
              cipher: unlockSecondPart,
            }
          ).then((response) => {
            // Convert hex string to byte array
            const unlockBytes = hexToUint8Array(response.data.unlock_code)
            writeAtCommandFrameRawBytes(unlockBytes, port)()
          })
        }
      }
    }

    reader.onErrorResponse = (err) =>
      setErrorMsg({
        open: true,
        message: err.message,
        severity: "error",
      })

    // Save the reader to the ref
    readerRef.current = reader

    // Start up the reader
    reader.startPortReader()

    // toggle mode in UI state if needed
    if (reader.getMode() !== currentMode) {
      setCurrentMode(reader.getMode())
    }
    registerReader(reader)
  }

  useEffect(() => {
    if (!connected) {
      setupReader().then(async () => {
        port.addEventListener("disconnect", () => {
          // tell parent to refresh ports
          refreshPorts()
        })
        setConnected(true)
        setDeviceStatus(DeviceStatuses.CONNECTED)
      })
    } else {
      // Get pod serial and set in state
      writeAtCommandFrame("@get_pod_serial", port)()
    }
  }, [connected])

  useEffect(() => {
    podSerialRef.current = podSerial
  }, [podSerial])

  useEffect(() => {
    deviceStatusRef.current = deviceStatus
  }, [deviceStatus])

  const [device, setDevice] = useState({
    arn: "",
    certificate_id: "",
    pem: "",
    key: "",
    usb_key: "",
  })

  async function getDeviceDetails() {
    const deviceResponse = await makeAuthorizedRequest(`devices/${podSerial}`)
    if (deviceResponse.status === 200) {
      setDevice(deviceResponse.data)
    } else {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Error getting device details",
      })
    }
  }

  const handleUnlockDevice = async () => {
    setDeviceStatus(DeviceStatuses.UNLOCKING)
    writeAtCommandFrame("@unlock", port)()
  }

  const waitForDeviceStatus = async (status: DeviceStatuses) => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (deviceStatusRef.current === status) {
          clearInterval(interval)
          resolve(true)
        } else {
          reject()
        }
      }, 3000)
    })
  }

  const handleSetUsbKey = async (usb_key: string) => {
    console.log("setting usb key", usb_key)
    await writeAtCommandFrame(`@add_key=${usb_key}`, port)()
  }

  const handleCreateDevice = async () => {
    if (podSerial === "") {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Pod Serial is required",
      })
      return
    }
    await handleUnlockDevice()
    if (!(await waitForDeviceStatus(DeviceStatuses.UNLOCKED))) {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Unlock failed",
      })
      return
    }
    const response = await makeAuthorizedRequest("devices", "POST", {
      device_id: podSerial,
    })
    if (response.status === 201) {
      setSnackAlert({
        open: true,
        severity: "success",
        message: "Device created successfully",
      })
      await getDeviceDetails()
    } else {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Device creation failed",
      })
    }
    console.log(response)
    await handleSetUsbKey(response.data.usb_key)
  }

  const sendCertManagerCommand = (
    command: CertificateCommand,
    secTag?: number,
    certType?: CertificateType,
    certContent?: string
  ) => {
    let atCommand = ""

    switch (command) {
      case CertificateCommand.WRITE:
        atCommand = `AT%CMNG=${command},${secTag},${certType},"${certContent}"`
        break
      case CertificateCommand.LIST:
        atCommand = `AT%CMNG=${command}`
        break
      case CertificateCommand.DELETE:
        atCommand = `AT%CMNG=${command},${secTag},${certType}`
        break
      default:
        return
    }

    writeAtCommandFrame(atCommand, port)()
  }

  const writeCertificate = async (
    certType: CertificateType,
    certContent: string
  ) => {
    console.log("writing cert", certType, certContent)
    sendCertManagerCommand(
      CertificateCommand.WRITE,
      SEC_TAG,
      certType,
      certContent
    )
  }

  const deleteCertificate = async (certType: CertificateType) => {
    sendCertManagerCommand(CertificateCommand.DELETE, SEC_TAG, certType)
  }

  const handleCreateCertificate = async () => {
    await handleUnlockDevice()
    if (!(await waitForDeviceStatus(DeviceStatuses.UNLOCKED))) {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Unlock failed",
      })
      return
    }
    const response = await makeAuthorizedRequest(
      `devices/${podSerial}/cert`,
      "POST",
      {
        device_id: podSerial,
      }
    )
    if (response.status === 201) {
      setSnackAlert({
        open: true,
        severity: "success",
        message: "Certificate issued successfully",
      })
    } else {
      setSnackAlert({
        open: true,
        severity: "error",
        message: "Certificate issue failed",
      })
    }

    try {
      writeAtCommandFrame(`@nrf`, port)()
      if (!(await waitForDeviceStatus(DeviceStatuses.NRF_MODE))) {
        setSnackAlert({
          open: true,
          severity: "error",
          message: "Error entering nRF mode",
        })
      }
      // wait 15 seconds for NRF to boot up
      await sleep(15)
      console.log("Erasing certs")
      setDeviceStatus(DeviceStatuses.ERASING)
      await deleteCertificate(CertificateType.CA_CERT)
      await sleep(2)
      await deleteCertificate(CertificateType.DEV_CERT)
      await sleep(2)
      await deleteCertificate(CertificateType.PRIV_KEY)
      await sleep(2)
      setDeviceStatus(DeviceStatuses.ERASED)

      setDeviceStatus(DeviceStatuses.SENDIND_CERTS)
      await writeCertificate(CertificateType.CA_CERT, response.data.root_ca)
      await sleep(2)
      await writeCertificate(CertificateType.DEV_CERT, response.data.pem)
      await sleep(2)
      await writeCertificate(
        CertificateType.PRIV_KEY,
        response.data.private_key
      )
      await sleep(2)
      setDeviceStatus(DeviceStatuses.SENT_CERTS)
    } catch (e) {
      setSnackAlert({
        open: true,
        severity: "error",
        message: `Error sending certs`,
      })
    }

    setDevice({
      arn: device.arn,
      certificate_id: device.certificate_id,
      pem: response.data.pem,
      key: response.data.private_key,
      usb_key: device.usb_key,
    })
  }

  useEffect(() => {
    // fetch data
    const dataFetch = async () => {
      const deviceResponse = await makeAuthorizedRequest(`devices/${podSerial}`)

      if (deviceResponse.status === 200) {
        setDevice(deviceResponse.data)
      } else {
        setSnackAlert({
          open: true,
          severity: "error",
          message: "Error getting device details",
        })
      }
    }

    if (podSerial) dataFetch()
  }, [podSerial])

  return (
    <UploadBox elevation={1} className="upload-ui">
      <h1>Device Details</h1>
      <h2>Pod Serial: {podSerial}</h2>
      <h2>{deviceStatus}</h2>
      <h2>{device.arn}</h2>
      <p>{device.certificate_id}</p>
      <p>{device.pem}</p>
      <p>{device.key}</p>
      <p>{device.usb_key}</p>

      <Button onClick={handleCreateDevice}>Create Device</Button>
      <Button onClick={handleCreateCertificate}>Issue Certificate</Button>
      <SnackAlerts />
    </UploadBox>
  )
}
